import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { fetchAssortmentProductId } from './api';
import { AssortmentRedirectError } from './AssortmentRedirectView/AssortmentRedirectError';
import {
  assortmentProductSearchParamsSchema,
  assortmentProductRedirectSearchParamsSchema,
  assortmentSetDetailsParamsSchema,
  categoriesSearchParamsSchema,
  offersSearchParamsSchema,
  assortmentSetsParamsSchema,
} from './schemas';
import { menuLayoutRoute } from '../../root/MenuLayout';
import RequireSingleCountry from '../../root/RequireSingleCountry';

const assortmentMainRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  path: 'assortment',
  component: RequireSingleCountry,
});

export const offersRoute = createRoute({
  getParentRoute: () => assortmentMainRoute,
  path: '/',
  validateSearch: offersSearchParamsSchema,
  component: lazyRouteComponent(() => import('features/assortment/OffersView/OffersView')),
});

const categoriesRoute = createRoute({
  getParentRoute: () => assortmentMainRoute,
  path: 'categories',
  validateSearch: categoriesSearchParamsSchema,
  component: lazyRouteComponent(() => import('features/assortment/CategoriesView/CategoriesView')),
});

const assortmentSetsRoute = createRoute({
  getParentRoute: () => assortmentMainRoute,
  path: 'sets',
  validateSearch: assortmentSetsParamsSchema,
  component: lazyRouteComponent(
    () => import('features/assortment/AssortmentSetsView/AssortmentSetsView'),
  ),
});

const assortmentSetDetailsRoute = createRoute({
  getParentRoute: () => assortmentMainRoute,
  path: 'sets/$setId',
  params: z.object({
    setId: z.string(),
  }),
  validateSearch: assortmentSetDetailsParamsSchema,
  component: lazyRouteComponent(
    () => import('features/assortment/AssortmentSetDetailsView/AssortmentSetDetailsView'),
  ),
});

export const assortmentProductRoute = createRoute({
  getParentRoute: () => assortmentMainRoute,
  path: 'products/$assortmentProductId',
  validateSearch: assortmentProductSearchParamsSchema,
  params: z.object({
    assortmentProductId: z.string(),
  }),
  component: lazyRouteComponent(() => import('./AssortmentProductView/AssortmentProductView')),
});

const assortmentProductRedirectRoute = createRoute({
  getParentRoute: () => assortmentMainRoute,
  path: 'products/$countryCode/$productId',
  params: z.object({
    countryCode: z.string(),
    productId: z.string(),
  }),
  validateSearch: assortmentProductRedirectSearchParamsSchema,
  component: () => null,
  errorComponent: ({ reset }) => <AssortmentRedirectError reset={reset} />,
  loader: async ({ params: { countryCode, productId }, location }) => {
    const data = await fetchAssortmentProductId(countryCode, productId);
    const { from, landingPage = '' } = location.search as z.infer<
      typeof assortmentProductRedirectSearchParamsSchema
    >;

    throw redirect({
      to: assortmentProductRoute.to + '/' + landingPage,
      params: { assortmentProductId: data.id },
      search: { from },
    });
  },
});

const assortmentProductOfferVariantsRoute = createRoute({
  getParentRoute: () => assortmentProductRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./AssortmentProductView/OfferVariantsView/OfferVariantsView'),
  ),
});

const assortmentProductDiscountRulesRoute = createRoute({
  getParentRoute: () => assortmentProductRoute,
  path: 'discount-rules',
  component: lazyRouteComponent(
    () => import('./AssortmentProductView/DiscountRulesView/DiscountRulesView'),
  ),
});

const assortmentProductPromotionsRoute = createRoute({
  getParentRoute: () => assortmentProductRoute,
  path: 'promotions',
  component: lazyRouteComponent(
    () => import('./AssortmentProductView/PromotionsView/PromotionsView'),
  ),
});

export const assortmentRouteTree = assortmentMainRoute.addChildren({
  offersRoute,
  categoriesRoute,
  assortmentSetsRoute,
  assortmentSetDetailsRoute,
  assortmentProductRoute: assortmentProductRoute.addChildren({
    assortmentProductOfferVariantsRoute,
    assortmentProductDiscountRulesRoute,
    assortmentProductPromotionsRoute,
  }),
  assortmentProductRedirectRoute,
});
